import { useEffect, useState, useCallback } from "react";
import {
  pushSupported, notificationPermission, getExistingSubscription,
  enablePushNotifications, disablePushNotifications, registerServiceWorker,
} from "@/lib/push";

/**
 * usePushNotifications — encapsulates browser push state.
 * Returns:
 *   supported    : boolean — browser can do push
 *   permission   : "default"|"granted"|"denied"|"unsupported"
 *   subscribed   : boolean — we have a live subscription for this device
 *   loading      : boolean — async in progress
 *   enable()     : async — request permission + subscribe + register with server
 *   disable()    : async — unsubscribe + tell server
 */
export function usePushNotifications() {
  const [supported] = useState(() => pushSupported());
  const [permission, setPermission] = useState(() => (supported ? notificationPermission() : "unsupported"));
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!supported) return;
    setPermission(notificationPermission());
    const sub = await getExistingSubscription();
    setSubscribed(!!sub);
  }, [supported]);

  useEffect(() => {
    // Make sure the service worker is registered so getSubscription works
    if (supported) registerServiceWorker().then(refresh);
  }, [supported, refresh]);

  const enable = useCallback(async () => {
    setLoading(true);
    const result = await enablePushNotifications();
    await refresh();
    setLoading(false);
    return result;
  }, [refresh]);

  const disable = useCallback(async () => {
    setLoading(true);
    await disablePushNotifications();
    await refresh();
    setLoading(false);
  }, [refresh]);

  return { supported, permission, subscribed, loading, enable, disable };
}
